import { useParams } from "react-router-dom";
import {
  useGo,
  useInvalidate,
  useList,
  useNavigation,
  useOne,
  useUpdate,
} from "@refinedev/core";
import { useState } from "react";
import { Flex } from "antd";
import { Breadcrumb, Spinner } from "@libertify/ui";
import { PlaybookForm } from "./components/PlaybookForm";
import {
  BrandKit,
  ContactResponse,
  SavedSettingsResponse,
  SavedSettingsUpdateRequest,
} from "../media/types";
import { useTranslate } from "#src/hooks";
import { useSession } from "hooks/useSession";
import { useOrganization } from "hooks/useOrganization";
import { useGroupedThemes } from "./hooks/useGroupedThemes";

export const PlaybooksEditPage = () => {
  const { id } = useParams();
  const go = useGo();
  const { list } = useNavigation();
  const t = useTranslate();
  const { currentOrganization } = useSession();
  const organizationId = currentOrganization?.id;
  // Org preferences hold the default brandkit used to preselect the picker.
  const { organizationPreferences, preferencesLoading } = useOrganization();
  const defaultBrandKitId = (
    organizationPreferences as { brand_kit_id?: string | null } | undefined
  )?.brand_kit_id;
  const updateMutation = useUpdate();
  const invalidate = useInvalidate();
  const [isSaving, setIsSaving] = useState(false);

  const { data: playbookData, isLoading: playbookLoading } =
    useOne<SavedSettingsResponse>({
      resource: `media/${organizationId}/saved-settings`,
      id,
      queryOptions: { enabled: !!organizationId && !!id },
    });

  const { data: brandKits, isLoading: brandKitsLoading } = useList<BrandKit>({
    resource: `media/${organizationId}/brand_kits?include_generated=false`,
    queryOptions: { enabled: !!organizationId },
  });

  const { data: contacts, isLoading: contactsLoading } =
    useList<ContactResponse>({
      resource: `media/${organizationId}/contacts`,
      queryOptions: { enabled: !!organizationId },
    });

  const {
    isLoading: themesLoading,
    publicThemes,
    orgThemes,
  } = useGroupedThemes(organizationId);

  const playbook = playbookData?.data;

  const handleUpdatePlaybook = async (values: SavedSettingsUpdateRequest) => {
    if (!organizationId || !id) return;

    setIsSaving(true);
    try {
      await updateMutation.mutateAsync({
        resource: `media/${organizationId}/saved-settings`,
        id,
        values,
        successNotification: {
          message: t("pages.playbooks.settingsUpdatedSuccessfully"),
          type: "success",
          description: t("pages.playbooks.success"),
        },
        errorNotification: {
          message: t("pages.playbooks.settingsUpdateFailed"),
          type: "error",
        },
      });

      await invalidate({
        resource: `media/${organizationId}/saved-settings`,
        invalidates: ["list"],
      });
    } catch (error) {
      console.error("Update failed:", error);
    } finally {
      setIsSaving(false);
    }
  };

  if (
    !organizationId ||
    playbookLoading ||
    brandKitsLoading ||
    contactsLoading ||
    themesLoading ||
    preferencesLoading
  ) {
    return (
      <Flex justify="center" align="center" style={{ height: "100vh" }}>
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (!playbook) {
    return null;
  }

  return (
    <Flex vertical gap={40} style={{ width: "100%", padding: "24px" }}>
      <Breadcrumb
        items={[
          {
            label: t("pages.playbooks.home"),
            onClick: () => go({ to: "/" }),
          },
          {
            label: t("pages.playbooks.title"),
            onClick: () => list("playbooks"),
          },
          {
            label: playbook.name,
          },
        ]}
      />
      <PlaybookForm
        key={playbook.id}
        playbook={playbook}
        contacts={contacts?.data || []}
        brandKits={brandKits?.data || []}
        publicThemes={publicThemes}
        orgThemes={orgThemes}
        onSave={(values) => handleUpdatePlaybook(values)}
        isSaving={isSaving}
        isLoading={playbookLoading}
        defaultBrandKitId={defaultBrandKitId}
      />
    </Flex>
  );
};
